<template>
  <div v-if="step === 'eligibility' || step === 'referred'">
    <FormErrors :errors="errors" />

    <h1 v-if="!isCollegeConfidential" class="uc-form-header">
      {{
        customEligibilityHeader ||
          $t('signUp.studentForm.eligibility.eligibilityHeader')
      }}
    </h1>
    <div v-if="showBigFutureIntroCopy">
      <p class="uc-form-text">
        {{ bfIntroCopy }}
      </p>
    </div>
    <div v-if="isCollegeConfidential">
      <div v-if="ccIntroCopy === 'get-that-a'">
        <h1 class="uc-form-header">
          {{
            $t('signUp.studentForm.eligibility.isCollegeConfidential.header')
          }}
        </h1>
        <p class="uc-form-text">
          {{ $t('signUp.studentForm.eligibility.isCollegeConfidential.text') }}
        </p>
      </div>
      <div v-else-if="ccIntroCopy === 'stuck-on-problem'">
        <h1 class="uc-form-header">
          {{ $t('signUp.studentForm.eligibility.stuckOnProblem.header') }}
        </h1>
        <p class="uc-form-text">
          {{ $t('signUp.studentForm.eligibility.stuckOnProblem.text') }}
        </p>
      </div>
      <div v-else-if="ccIntroCopy === 'grade-focused'">
        <h1 class="uc-form-header">
          {{ $t('signUp.studentForm.eligibility.gradeFocused.header') }}
        </h1>
        <p class="uc-form-text">
          {{ $t('signUp.studentForm.eligibility.gradeFocused.text') }}
        </p>
      </div>
      <div v-else>
        <h1 class="uc-form-header">
          {{ $t('signUp.studentForm.eligibility.header') }}
        </h1>
        <p class="uc-form-text">
          {{ $t('signUp.studentForm.eligibility.text') }}
        </p>
      </div>
    </div>
    <form
      id="form-eligibility"
      class="uc-column"
      @submit.prevent="
        isReferred ? submitReferralEligibilityForm() : submitEligibilityForm()
      "
    >
      <div class="uc-form-element">
        <div class="uc-row justify-between">
          <label
            for="grade"
            v-bind:class="{
              error: hasFormValidationError(v$.eligibility.currentGrade),
            }"
          >
            {{ $t('signUp.studentForm.eligibility.grade.label') }}</label
          >
          <div
            v-if="hasFormValidationError(v$.eligibility.currentGrade)"
            class="error-caption"
          >
            {{ getFormValidationError(v$.eligibility.currentGrade) }}
          </div>
        </div>
        <v-select
          id="grade"
          class="uc-form-select-input"
          v-model="eligibility.currentGrade"
          :placeholder="$t('signUp.studentForm.eligibility.grade.placeholder')"
          :aria-label="$t('signUp.studentForm.eligibility.grade.ariaLabel')"
          :options="gradeStates"
          :reduce="option => option.value"
          :searchable="false"
          :clearable="false"
          v-bind:class="{
            'uc-form-select-input-invalid': hasFormValidationError(
              v$.eligibility.currentGrade
            ),
          }"
          @close="onGradeClose"
          required
        ></v-select>
      </div>

      <div class="uc-form-element">
        <div class="uc-row justify-between">
          <label
            for="school"
            v-bind:class="{
              error: hasFormValidationError(v$.eligibility.highSchool),
            }"
            >{{ $t('signUp.studentForm.eligibility.school.label') }}</label
          >
          <div
            v-if="hasFormValidationError(v$.eligibility.highSchool)"
            class="error-caption"
          >
            {{ getFormValidationError(v$.eligibility.highSchool) }}
          </div>
        </div>
        <input
          id="school"
          class="uc-form-text-input"
          type="text"
          :placeholder="$t('signUp.studentForm.eligibility.school.placeholder')"
          v-model="eligibility.highSchool"
          v-bind:class="{
            'uc-form-text-input-invalid': hasFormValidationError(
              v$.eligibility.highSchool
            ),
          }"
          @blur="v$.eligibility.highSchool.$touch"
          required
        />
        <!-- <autocomplete
          id="school"
          base-class="uc-form-autocomplete-input"
          :placeholder="$t('signUp.studentForm.eligibility.school.placeholder')"
          :aria-label="$t('signUp.studentForm.eligibility.school.placeholder')"
          @submit="handleSelectHighSchool"
          @blur="v$.eligibility.highSchool.$touch"
          v-bind:class="{
            'uc-form-autocomplete-input-invalid': hasFormValidationError(
              v$.eligibility.highSchool
            ),
          }"
          required
        >
          <template #result="{ result, props }">
            <li v-bind="props">
              <div v-if="result.name" id="ph-no-capture" class="result">
                {{ result.name }} ({{ result.city }}, {{ result.state }})
              </div>
              <a
                v-if="result.cantFindSchool"
                target="_blank"
                href="https://upchieve.org/cant-find-school"
              >
                <div class="result">
                  {{ CANNOT_FIND_SCHOOL_TEXT }}
                </div>
              </a>
            </li>
          </template>
        </autocomplete> -->
      </div>

      <div class="uc-form-element">
        <div class="uc-row justify-between">
          <label
            for="zipCode"
            v-bind:class="{
              error: hasFormValidationError(v$.eligibility.zipCode),
            }"
            >{{ $t('signUp.studentForm.eligibility.zipCode.label') }}</label
          >
          <div
            v-if="hasFormValidationError(v$.eligibility.zipCode)"
            class="error-caption"
          >
            {{ getFormValidationError(v$.eligibility.zipCode) }}
          </div>
        </div>
        <input
          id="zipCode"
          class="uc-form-text-input"
          type="text"
          :placeholder="
            $t('signUp.studentForm.eligibility.zipCode.placeholder')
          "
          v-model="eligibility.zipCode"
          v-bind:class="{
            'uc-form-text-input-invalid': hasFormValidationError(
              v$.eligibility.zipCode
            ),
          }"
          @blur="v$.eligibility.zipCode.$touch"
          required
        />
      </div>

      <div v-if="!skipEligibilityEmail" class="uc-form-element">
        <div class="uc-row justify-between">
          <label
            for="email"
            v-bind:class="{
              error: hasFormValidationError(v$.eligibility.email),
            }"
            >{{ $t('signUp.studentForm.eligibility.email.label') }}</label
          >
          <div
            v-if="hasFormValidationError(v$.eligibility.email)"
            class="error-caption"
          >
            {{ getFormValidationError(v$.eligibility.email) }}
          </div>
        </div>
        <input
          id="email"
          class="uc-form-text-input"
          type="email"
          :placeholder="$t('signUp.studentForm.eligibility.email.placeholder')"
          v-model="eligibility.email"
          v-bind:class="{
            'uc-form-text-input-invalid': hasFormValidationError(
              v$.eligibility.email
            ),
          }"
          @blur="v$.eligibility.email.$touch"
          required
        />
        <div class="metadata">
          {{ $t('signUp.studentForm.eligibility.email.metadata') }}
        </div>
      </div>

      <button
        id="btn-eligibility-submit"
        class="uc-form-button"
        :disabled="cannotSubmitForm(v$.eligibility)"
        type="submit"
      >
        {{ $t('general.continue') }}
      </button>
    </form>
    <i18n
      path="signUp.notReferred.text-4"
      tag="p"
      class="uc-form-text below text-center"
    >
      <router-link class="uc-link" :to="`/${$i18n.locale}/login`">{{
        $t('signUp.notReferred.login')
      }}</router-link>
    </i18n>
  </div>

  <div
    v-else-if="step === 'eligible'"
    class="uc-column justify-center items-center h-full"
  >
    <verification-badge />
    <h1 class="uc-form-header center">
      {{ $t('signUp.studentForm.eligible.header') }}
    </h1>
    <p class="uc-form-text center">
      {{ $t('signUp.studentForm.eligible.text') }}
    </p>
    <button class="uc-form-button" @click="accountPage">
      {{ $t('general.continue') }}
    </button>
  </div>

  <div
    v-else-if="step === 'ineligible'"
    class="uc-column justify-center items-center h-full"
  >
    <error-badge />
    <h1 class="uc-form-header center">{{ title }}</h1>

    <p
      v-if="isCollegeStudent"
      class="small-paragraph"
      v-html="$t('signUp.studentForm.ineligible.isCollegeStudentText')"
    />

    <p
      v-else
      class="small-paragraph"
      v-html="$t('signUp.studentForm.ineligible.text')"
    />

    <p
      v-if="isCollegeStudent"
      class="small-paragraph"
      v-html="$t('signUp.studentForm.ineligible.link')"
    />

    <button
      v-if="!isCollegeStudent"
      class="uc-form-button"
      type="button"
      @click="ineligibleContinue"
    >
      {{ $t('general.continue') }}
    </button>
  </div>

  <div v-else-if="step === 'account'">
    <FormErrors :errors="errors" />

    <h1 class="uc-form-header">
      {{ $t('signUp.studentForm.account.header') }}
    </h1>
    <i18n path="signUp.notReferred.text-4" tag="p" class="uc-form-text">
      <router-link class="uc-link" :to="`/${$i18n.locale}/login`">{{
        $t('signUp.notReferred.login')
      }}</router-link>
    </i18n>

    <div v-if="offerGoogleSSO">
      <button
        class="uc-form-button google"
        @click.prevent="signUpWithGoogle"
        :disabled="isSubmittingAccountForm"
      >
        <google-logo />
        {{ $t('signUp.studentForm.account.offerGoogleSSO.button') }}
      </button>
      <p
        class="terms-text"
        v-html="$t('signUp.studentForm.account.offerGoogleSSO.text')"
      />

      <div class="uc-row items-center w-full break-line-container">
        <span class="break-line w-full"></span>
        <p class="or-text">{{ $t('general.or') }}</p>
        <span class="break-line w-full"></span>
      </div>
    </div>

    <form
      aria-label="Create student account"
      @submit.prevent="submitAccountForm()"
    >
      <div class="uc-form-element">
        <div class="uc-row justify-between">
          <label
            for="firstName"
            v-bind:class="{
              error: hasFormValidationError(v$.profile.firstName),
            }"
            >{{ $t('signUp.studentForm.account.firstNameLabel') }}</label
          >
          <div
            v-if="hasFormValidationError(v$.profile.firstName)"
            class="error-caption"
          >
            {{ getFormValidationError(v$.profile.firstName) }}
          </div>
        </div>
        <input
          id="firstName"
          class="uc-form-text-input"
          type="text"
          :placeholder="$t('signUp.studentForm.account.firstNamePlaceholder')"
          v-model="profile.firstName"
          v-bind:class="{
            'uc-form-text-input-invalid': hasFormValidationError(
              v$.profile.firstName
            ),
          }"
          @blur="v$.profile.firstName.$touch"
          required
        />
      </div>

      <div class="uc-form-element">
        <div class="uc-row justify-between">
          <label
            for="lastName"
            v-bind:class="{
              error: hasFormValidationError(v$.profile.lastName),
            }"
            >{{ $t('signUp.studentForm.account.lastNameLabel') }}</label
          >
          <div
            v-if="hasFormValidationError(v$.profile.lastName)"
            class="error-caption"
          >
            {{ getFormValidationError(v$.profile.lastName) }}
          </div>
        </div>
        <input
          id="lastName"
          class="uc-form-text-input"
          type="text"
          :placeholder="$t('signUp.studentForm.account.lastNamePlaceholder')"
          v-model="profile.lastName"
          v-bind:class="{
            'uc-form-text-input-invalid': hasFormValidationError(
              v$.profile.lastName
            ),
          }"
          @blur="v$.profile.lastName.$touch"
          required
        />
      </div>

      <div class="uc-form-element">
        <div class="uc-row justify-between">
          <label
            for="grade"
            v-bind:class="{
              error: hasFormValidationError(v$.eligibility.currentGrade),
            }"
          >
            {{ $t('signUp.studentForm.eligibility.grade.label') }}</label
          >
          <div
            v-if="hasFormValidationError(v$.eligibility.currentGrade)"
            class="error-caption"
          >
            {{ getFormValidationError(v$.eligibility.currentGrade) }}
          </div>
        </div>
        <v-select
          id="grade"
          class="uc-form-select-input"
          v-model="eligibility.currentGrade"
          :placeholder="$t('signUp.studentForm.eligibility.grade.placeholder')"
          :aria-label="$t('signUp.studentForm.eligibility.grade.ariaLabel')"
          :options="gradeStates"
          :reduce="option => option.value"
          :searchable="false"
          :clearable="false"
          v-bind:class="{
            'uc-form-select-input-invalid': hasFormValidationError(
              v$.eligibility.currentGrade
            ),
          }"
          @close="onGradeClose"
          required
        ></v-select>
      </div>

      <div class="uc-form-element">
        <div class="uc-row justify-between">
          <label
            for="email"
            v-bind:class="{
              error: hasFormValidationError(v$.credentials.email),
            }"
          >
            {{ $t('signUp.studentForm.account.emailLabel') }}
          </label>
          <div
            v-if="hasFormValidationError(v$.credentials.email)"
            class="error-caption"
          >
            {{ getFormValidationError(v$.credentials.email) }}
          </div>
        </div>
        <input
          id="email"
          class="uc-form-text-input"
          type="email"
          :placeholder="$t('signUp.studentForm.account.emailPlaceholder')"
          v-model="credentials.email"
          v-bind:class="{
            'uc-form-text-input-invalid': hasFormValidationError(
              v$.credentials.email
            ),
          }"
          @blur="v$.credentials.email.$touch"
          required
        />
      </div>

      <div class="uc-form-element">
        <label for="phoneNumber" class="uc-form-label">{{
          $t('signUp.volunteerForm.step-2.phoneNumber')
        }}</label>
        <vue-phone-number-input
          id="phoneNumber"
          class="phone-input"
          v-model="profile.phone"
          :required="true"
          :error="
            invalidInputs.indexOf('phone') > -1 && !phoneInputInfo.isValid
          "
          default-country-code="IR"
          no-country-selector
          clearable
          color="#555"
          valid-color="#16ba97"
          @update="onPhoneInputUpdate"
        />
      </div>

      <div class="uc-form-element">
        <div class="uc-row justify-between">
          <label
            for="password"
            v-bind:class="{
              error: hasFormValidationError(v$.credentials.password),
            }"
            >{{ $t('signUp.studentForm.account.passwordLabel') }}</label
          >
          <div
            v-if="hasFormValidationError(v$.credentials.password)"
            class="error-caption"
          >
            {{ getFormValidationError(v$.credentials.password) }}
          </div>
        </div>
        <input
          id="password"
          class="uc-form-text-input"
          type="password"
          :placeholder="$t('signUp.studentForm.account.passwordPlaceholder')"
          v-model="credentials.password"
          v-bind:class="{
            'uc-form-text-input-invalid': hasFormValidationError(
              v$.credentials.password
            ),
          }"
          @blur="v$.credentials.password.$touch"
          required
        />
        <div
          class="metadata"
          v-bind:class="{
            'metadata error': hasFormValidationError(v$.credentials.password),
          }"
        >
          {{ $t('signUp.studentForm.account.metadata') }}
        </div>
      </div>

      <button
        class="uc-form-button"
        :disabled="
          isSubmittingAccountForm ||
            cannotSubmitForm(v$.profile) ||
            cannotSubmitForm(v$.credentials)
        "
        type="submit"
      >
        {{ $t('signUp.studentForm.account.createAccountButton') }}
      </button>
      <p
        class="terms-text"
        v-html="$t('signUp.studentForm.account.termsText')"
      />
    </form>
  </div>

  <div
    v-else-if="step === 'verification'"
    class="uc-column justify-center items-center h-full"
  >
    <FormErrors :errors="errors" />

    <p
      class="uc-form-header center"
      v-html="
        $t('verification.sendTo', { sendTo: phoneInputInfo.formatNational })
      "
    />
    <form
      aria-label="confirm verification code"
      @submit.prevent="confirmVerificationCode()"
    >
      <div class="uc-form-element">
        <label for="verification-code">
          {{ $t('verification.label') }}
        </label>

        <SimpleOtpInput
          v-model="verificationCode"
          class="otp-with-effect"
          inputClasses="input-with-effect"
          :pasteDelayMs="192"
          withWebOtp
        >
          <template v-slot:extra>
            <span class="focus-border">
              <i></i>
            </span>
          </template>
        </SimpleOtpInput>
        <!-- <input
          id="verification-code"
          class="uc-form-text-input"
          v-model="verificationCode"
          type="text"
          placeholder="XXXXXX"
          required
        /> -->
      </div>

      <div class="uc-form-subtext verification__sub-text text-center">
        {{ $t('verification.notReceiveText') }}
        <button
          :disabled="isSubmittingVerification || !countDownFlag"
          @click.prevent="sendCode"
          class="uc-link asText"
          :class="{ disableButton: !countDownFlag }"
          type="text"
        >
          {{ $t('verification.sendCodeButton') }}
          <span id="timer"></span>
        </button>
      </div>

      <button
        class="uc-form-button"
        :disabled="
          isSubmittingVerification || cannotSubmitForm(v$.verificationCodeTrim)
        "
        type="submit"
      >
        {{ $t('verification.confirmVerificationCodeButton') }}
      </button>
    </form>
  </div>

  <div
    v-else-if="step === 'international'"
    class="uc-column justify-center items-center"
  >
    <error-badge />
    <h1 class="uc-form-header center">
      Looks like you're not in <br />the U.S.!
    </h1>

    <p class="international-availability-info">
      UPchieve is currently only available to students in the U.S. We're sorry
      for the inconvenience! 😔
    </p>

    <p class="international-contact-us">
      Live in the U.S. and still seeing this message? Make sure you're not using
      a VPN.
      <router-link :to="`/${$i18n.locale}/contact`" class="uc-link"
        >Contact Us</router-link
      >
      if you still need help!
    </p>
  </div>
  <div v-else class="uc-form-body">
    {{ $t('signUp.studentForm.errorText') }}
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { useVuelidate } from '@vuelidate/core'
import {
  email,
  helpers,
  maxLength,
  minLength,
  required,
  requiredIf,
} from '@vuelidate/validators'
// import Autocomplete from '@trevoreyre/autocomplete-vue'
import LoggerService from '@/services/LoggerService'
import AuthService from '@/services/AuthService'
import NetworkService from '@/services/NetworkService'
import AnalyticsService from '@/services/AnalyticsService'
import VerificationBadge from '@/assets/verification.svg'
import ErrorBadge from '@/assets/error_badge.svg'
import GoogleLogo from '@/assets/google_logo.svg'
import {
  EVENTS,
  GRADES_NEW,
  FA_GRADES_NEW,
  VERIFICATION_METHOD,
} from '@/consts'
import FormErrors from '@/components/FormErrors.vue'
import config from '../../config'
import i18n from '@/i18n'
import VuePhoneNumberInput from 'vue-phone-number-input'
import SimpleOtpInput from 'vue-simple-otp-input'

export default {
  components: {
    // Autocomplete,
    VerificationBadge,
    ErrorBadge,
    GoogleLogo,
    FormErrors,
    VuePhoneNumberInput,
    SimpleOtpInput,
  },
  setup() {
    return { v$: useVuelidate() }
  },
  validations() {
    return {
      eligibility: {
        currentGrade: {
          required: helpers.withMessage(
            this.$root.$t('signUp.studentForm.validations.required'),
            required
          ),
        },
        highSchool: {
          required: helpers.withMessage(
            this.$root.$t('signUp.studentForm.validations.required'),
            required
          ),
        },
        zipCode: {
          required: helpers.withMessage(
            this.$root.$t('signUp.studentForm.validations.required'),
            required
          ),
          // minLength: helpers.withMessage(
          //   'Must be 10 characters long',
          //   minLength(10)
          // ),
          // maxLength: helpers.withMessage(
          //   'Must be 10 characters long',
          //   maxLength(10)
          // ),
        },
        email: {
          required: helpers.withMessage(
            this.$root.$t('signUp.studentForm.validations.required'),
            requiredIf(!this.skipEligibilityEmail)
          ),
          email: helpers.withMessage(
            this.$root.$t('signUp.studentForm.validations.invalidEmail'),
            email
          ),
        },
      },
      profile: {
        firstName: {
          required: helpers.withMessage(
            this.$root.$t('signUp.studentForm.validations.required'),
            required
          ),
        },
        lastName: {
          required: helpers.withMessage(
            this.$root.$t('signUp.studentForm.validations.required'),
            required
          ),
        },
      },
      credentials: {
        email: {
          required: helpers.withMessage(
            this.$root.$t('signUp.studentForm.validations.required'),
            required
          ),
          email: helpers.withMessage(
            this.$root.$t('signUp.studentForm.validations.invalidEmail'),
            email
          ),
        },
        password: {
          required: helpers.withMessage(
            this.$root.$t('signUp.studentForm.validations.required'),
            required
          ),
          isPasswordValid: helpers.regex(this.PASSWORD_PATTERN),
        },
      },
      verificationCodeTrim: {
        required: helpers.withMessage(
          this.$root.$t('signUp.studentForm.validations.required'),
          required
        ),
        minLength: helpers.withMessage(
          'Must be 6 characters long',
          minLength(6)
        ),
        maxLength: helpers.withMessage(
          'Must be 6 characters long',
          maxLength(6)
        ),
      },
    }
  },
  data() {
    return {
      timerOn: true,
      countDownFlag: true,
      verificationCode: '',
      msg: '',
      errors: [],
      invalidInputs: [],
      eligibility: {
        currentGrade: '',
        highSchool: '',
        // highSchool: {},
        zipCode: '',
        email: '',
      },
      credentials: {
        email: '',
        password: '',
      },
      profile: {
        firstName: '',
        lastName: '',
        phone: '',
      },
      phoneInputInfo: {},
      step: '',
      hasStartedSearchingForSchool: false,
      hasEnteredEmail: false,
      hasEnteredZipCode: false,
      hasEnteredFirstName: false,
      hasEnteredLastName: false,
      hasEnteredPassword: false,
      isCollegeStudent: false,
      isReferred: false,
      isSubmittingAccountForm: false,
      isSubmittingVerification: false,
      partnerKey: undefined,
      studentPartner: undefined,
      // Reach Studies:
      isCollegeConfidential: false,
      showBigFutureIntroCopy: false,
      skipEligibilityEmail: false,
    }
  },
  async mounted() {
    const params = this.$route.query
    if (params.partner) {
      try {
        const res = await NetworkService.getStudentPartner(params.partner)
        const studentPartner = res.data.studentPartner
        if (!studentPartner || studentPartner.deactivated) {
          delete params.partner
        } else {
          this.studentPartner = studentPartner
        }
      } catch {
        delete params.partner
      }
    }

    localStorage.removeItem('isSSOSignUpRedirect')
    if (this.isFailureRedirect()) {
      this.eligibility = {
        currentGrade: params['currentGrade'],
        highSchool: {
          upchieveId: params['highSchoolId'],
        },
        zipCode: params['zipCode'],
        email: params['email'],
      }
      this.partnerKey = params['partner']
      this.credentials.email = params['email']
      this.errors.push(
        this.$route.query['error'] ??
          this.$root.$t('signUp.studentForm.failedSignUpGoogle')
      )
      this.step = 'account'
    } else if (this.isReferred) this.step = 'referred'
    else this.accountPage(params)

    if (params['utm_source'] === 'collegeconfidential') {
      this.isCollegeConfidential = true
    }

    if (params['partner']) {
      this.partnerKey = params['partner']

      if (
        this.partnerKey === 'bigfuture' &&
        this.isBfIntroCopyEnabled &&
        this.bfIntroCopy
      ) {
        this.showBigFutureIntroCopy = true
      }
    }

    this.skipEligibilityEmail =
      this.eligibilityEmail && this.partnerKey === 'bigfuture'
    if (this.skipEligibilityEmail) {
      AnalyticsService.captureEvent(EVENTS.SKIPPING_ELIGIBILITY_EMAIL)
    }

    const isDomesticIpAddress = await this.isDomesticIpAddress()
    if (!isDomesticIpAddress) return this.internationalPage()
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
    }),
    ...mapGetters({
      dir: 'app/direction',
      isAutoFlowUser: 'user/isAutoFlowUser',
      offerGoogleSSO: 'featureFlags/offerGoogleSSO',
      ccIntroCopy: 'featureFlags/ccIntroCopy',
      isBfIntroCopyEnabled: 'featureFlags/isBfIntroCopyEnabled',
      bfIntroCopy: 'featureFlags/bfIntroCopy',
      eligibilityEmail: 'featureFlags/eligibilityEmail',
    }),
    trimCurrentGrade() {
      // extracting the first word out of the gradeLevels
      // example: "8th grade" --> "8th"
      return this.eligibility.currentGrade.split(' ')[0]
    },
    title() {
      if (this.isCollegeStudent)
        return this.$root.$t(
          'signUp.studentForm.ineligible.isCollegeStudentTitle'
        )
      else return this.$root.$t('signUp.studentForm.ineligible.title')
    },
    CANNOT_FIND_SCHOOL_TEXT() {
      return `Can't find your school?`
    },
    gradeStates() {
      if (this.dir == 'rtl') return FA_GRADES_NEW
      else return GRADES_NEW
    },
    PASSWORD_PATTERN() {
      return /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/
    },
    customEligibilityHeader() {
      if (this.studentPartner) {
        return this.$root.$t(
          'signUp.studentForm.eligibility.customEligibilityHeader',
          { studentPartnerName: this.studentPartner.name }
        )
      }
      return ''
    },
    verificationCodeTrim() {
      return this.verificationCode.trim()
    },
  },
  watch: {
    'eligibility.email': function(currentValue, oldValue) {
      if (currentValue && !oldValue) {
        if (!this.hasEnteredEmail)
          AnalyticsService.captureEvent(EVENTS.STUDENT_ENTERED_EMAIL)
        this.hasEnteredEmail = true
      }
    },
    'eligibility.zipCode': function(currentValue, oldValue) {
      if (currentValue && !oldValue) {
        if (!this.hasEnteredZipCode)
          AnalyticsService.captureEvent(EVENTS.STUDENT_ENTERED_ZIP_CODE)
        this.hasEnteredZipCode = true
      }
    },
    'profile.firstName'(currentValue, oldValue) {
      if (currentValue && !oldValue) {
        if (!this.hasEnteredFirstName)
          AnalyticsService.captureEvent(EVENTS.STUDENT_ENTERED_FIRST_NAME)
        this.hasEnteredFirstName = true
      }
    },
    'profile.lastName'(currentValue, oldValue) {
      if (currentValue && !oldValue) {
        if (!this.hasEnteredLastName)
          AnalyticsService.captureEvent(EVENTS.STUDENT_ENTERED_LAST_NAME)
        this.hasEnteredLastName = true
      }
    },
    'credentials.password'(currentValue, oldValue) {
      if (currentValue && !oldValue) {
        if (!this.hasEnteredPassword)
          AnalyticsService.captureEvent(EVENTS.STUDENT_ENTERED_PASSWORD)
        this.hasEnteredPassword = true
      }
    },
  },
  methods: {
    timer(remaining) {
      var m = Math.floor(remaining / 60)
      var s = remaining % 60

      m = m < 10 ? '0' + m : m
      s = s < 10 ? '0' + s : s
      document.getElementById('timer').innerHTML = m + ':' + s
      remaining -= 1

      if (remaining >= 0 && this.timerOn) {
        setTimeout(() => {
          this.countDownFlag = false
          this.timer(remaining)
        }, 1000)
        return
      }

      if (!this.timerOn) {
        // Do validate stuff here
        return
      }

      // Do timeout stuff here
      this.countDownFlag = true
    },
    async sendCode() {
      this.errors = []
      if (this.isSubmittingVerification) return
      this.isSubmittingVerification = true
      // this.loadingMessage = this.$root.$t('verification.loadingMessage')
      try {
        await AuthService.initiateVerificationV2({
          sendTo: this.phoneInputInfo.e164,
          verificationMethod: VERIFICATION_METHOD.SMS,
          userId: this.user.id,
          firstName: this.user.firstName,
        })

        this.timer(120)
        this.errors = []
      } catch (error) {
        const errorMsg =
          error?.response?.data?.err ??
          this.$root.$t('signUp.studentForm.responseError')
        this.errors.push(errorMsg)
      }

      this.isSubmittingVerification = false
      // this.loadingMessage = ''
    },
    isValidVerificationCode() {
      return !(
        this.verificationCode.length !== 6 ||
        isNaN(Number(this.verificationCode))
      )
    },
    onPhoneInputUpdate(phoneInputInfo) {
      this.phoneInputInfo = phoneInputInfo
    },
    logStudentClickedLearnMoreLink() {
      AnalyticsService.captureEvent(EVENTS.BF_STUDENT_CLICKED_LEARN_MORE)
    },
    isFailureRedirect() {
      return (
        !!this.$route.query['email'] &&
        !!this.$route.query['highSchoolId'] &&
        !!this.$route.query['zipCode'] &&
        !!this.$route.query['currentGrade']
      )
    },

    // Necessary to explicitly call on close of the currentGrade select menu
    // because v-select component is preventing blur event.
    onGradeClose() {
      if (!this.eligibility.currentGrade) {
        this.v$.eligibility.currentGrade.$touch()
      }
    },

    eligibilityPage(params) {
      this.step = 'eligibility'
      this.$router.replace({
        path: `/${i18n.locale}/sign-up/student/eligibility`,
        query: params,
      })
    },

    async isDomesticIpAddress() {
      return true
      // try {
      //   await NetworkService.checkIpAddress()
      //   return true
      // } catch (error) {
      //   return false
      // }
    },
    internationalPage() {
      this.step = 'international'
      this.$router.replace(`/${i18n.locale}/sign-up/student/international`)
    },
    async hasEligibilityFormErrors() {
      this.errors = []

      // if (!this.eligibility.highSchool.upchieveId) {
      //   this.errors.push('You must select a school.')
      // }

      // const zipCode = this.eligibility.zipCode

      // const {
      //   data: { isValidZipCode },
      // } = await NetworkService.checkZipCode({ zipCode })
      // if (!isValidZipCode) {
      //   this.errors.push('You must enter a valid United States zip code.')
      // }

      if (!this.eligibility.email && !this.skipEligibilityEmail) {
        this.errors.push(this.$root.$t('signUp.studentForm.emailRequiredError'))
      }

      if (!this.eligibility.currentGrade) {
        this.errors.push(this.$root.$t('signUp.studentForm.selectGradeError'))
      }

      return !!this.errors.length
    },
    // transitions from the referred sign up view to account view
    async submitReferralEligibilityForm() {
      if (await this.hasEligibilityFormErrors()) return
      this.accountPage()
    },
    async submitEligibilityForm() {
      AnalyticsService.captureEvent(EVENTS.STUDENT_CLICKED_CHECK_MY_ELIGIBILITY)

      // reset error msg from server
      this.msg = ''

      if (await this.hasEligibilityFormErrors()) return

      NetworkService.checkStudentEligibility({
        schoolUpchieveId: this.eligibility.highSchool.upchieveId,
        zipCode: this.eligibility.zipCode,
        email: this.eligibility.email,
        referredByCode: window.localStorage.getItem('upcReferredByCode'),
        currentGrade: this.trimCurrentGrade,
      })
        .then(async response => {
          const isEligible = response.data.isEligible
          if (isEligible) {
            AnalyticsService.captureEvent(EVENTS.ELIGIBILITY_ELIGIBLE, {
              event: EVENTS.ELIGIBILITY_ELIGIBLE,
            })
            // autofill the user's email
            this.credentials.email = this.eligibility.email
            this.step = 'eligible'
            this.$router.replace(`/${i18n.locale}/sign-up/student/eligible`)
          } else {
            AnalyticsService.captureEvent(EVENTS.ELIGIBILITY_INELIGIBLE, {
              event: EVENTS.ELIGIBILITY_INELIGIBLE,
            })
            if (response.data.isCollegeStudent) this.isCollegeStudent = true
            this.step = 'ineligible'
            this.$router.replace(`/${i18n.locale}/sign-up/student/ineligible`)
          }
          const isDomesticIpAddress = await this.isDomesticIpAddress()
          if (!isDomesticIpAddress) return this.internationalPage()
        })
        .catch(res => {
          const error =
            (res.body && (res.body.err || res.body.message)) ||
            this.$root.$t('signUp.studentForm.unknownServerError')
          this.errors.push(error)
        })
    },
    accountPage() {
      this.step = 'account'
      // this.$router.replace(`/${i18n.locale}/sign-up/student/account`)
    },
    verificationPage() {
      this.step = 'verification'
      // this.$router.replace(`/${i18n.locale}/sign-up/student/verification`)
    },
    ineligibleContinue() {
      AnalyticsService.captureEvent(EVENTS.STUDENT_CLICKED_STUDENT_ACCESS_PAGE)
      window.location = 'https://upchieve.org/request-access'
    },
    autocompleteSchool(input) {
      this.eligibility.highSchool = {}

      return new Promise(resolve => {
        if (input.length < 3) {
          return resolve([])
        }

        if (!this.hasStartedSearchingForSchool)
          AnalyticsService.captureEvent(EVENTS.STUDENT_SEARCHED_SCHOOL)
        this.hasStartedSearchingForSchool = true

        let cantFindSchoolItem = {
          cantFindSchool: true,
        }

        NetworkService.searchSchool({ query: input })
          .then(response => response.data.results)
          .then(schools => {
            schools.push(cantFindSchoolItem)
            resolve(schools)
          })
      })
    },
    getSchoolDisplayName(school) {
      if (school.cantFindSchool) {
        return
      }
      return `${school.name} (${school.city}, ${school.state})`
    },
    handleSelectHighSchool(school) {
      if (school.value === this.CANNOT_FIND_SCHOOL_TEXT) {
        AnalyticsService.captureEvent(EVENTS.STUDENT_CLICKED_CANT_FIND_SCHOOL)
      } else {
        AnalyticsService.captureEvent(EVENTS.STUDENT_SELECTED_SCHOOL)
        this.eligibility.highSchool = school || {}
      }
    },
    hasAccountFormErrors() {
      this.errors = []

      if (!this.profile.firstName || !this.profile.lastName) {
        this.errors.push(this.$root.$t('signUp.studentForm.nameError'))
      }
      if (!this.credentials.password) {
        this.errors.push(this.$root.$t('signUp.studentForm.passwordError'))
      }

      return !!this.errors.length
    },
    async submitAccountForm() {
      this.isSubmittingAccountForm = true
      if (this.hasAccountFormErrors()) {
        this.isSubmittingAccountForm = false
        return
      }

      try {
        await AuthService.registerStudentV2({
          email: this.credentials.email,
          firstName: this.profile.firstName,
          phone: this.phoneInputInfo.e164,
          gradeLevel: this.trimCurrentGrade,
          lastName: this.profile.lastName,
          password: this.credentials.password,
          referredByCode: window.localStorage.getItem('upcReferredByCode'),
          studentPartnerOrg: this.partnerKey,
        })
        window.localStorage.removeItem('upcReferredByCode')

        this.$store.dispatch('user/fetchUser')

        this.verificationPage()
        this.sendCode()
      } catch (e) {
        const errorMsg =
          e?.response?.data?.err ??
          this.$root.$t('signUp.studentForm.responseError')
        this.errors.push(errorMsg)
        if (errorMsg && errorMsg.match(/^Password/))
          AnalyticsService.captureEvent(EVENTS.STUDENT_ENTERED_INVALID_PASSWORD)
        if (e?.response?.status !== 422) {
          LoggerService.noticeError(e)
        }
      } finally {
        this.isSubmittingAccountForm = false
      }
    },
    async confirmVerificationCode() {
      this.errors = []
      if (this.isSubmittingVerification) return
      this.isSubmittingVerification = true

      if (!this.isValidVerificationCode) {
        this.errors.push(
          this.$root.$t('verification.isValidVerificationCodeError')
        )

        this.isSubmittingVerification = false
        return
      }

      try {
        const {
          data: { success },
        } = await AuthService.confirmVerificationV2({
          verificationCode: this.verificationCode,
          sendTo: this.phoneInputInfo.e164,
          verificationMethod: VERIFICATION_METHOD.SMS,
        })

        if (success) {
          AnalyticsService.captureEvent(EVENTS.ACCOUNT_VERIFIED, {
            verificationMethod: VERIFICATION_METHOD.SMS,
          })
          const userUpdates = { verified: true }

          userUpdates.phoneVerified = true

          this.$store.dispatch('user/firstDashboardVisit', true)
          this.$store.dispatch('user/addToUser', userUpdates)

          if (this.isAutoFlowUser) {
            this.$router.push(`/${i18n.locale}/welcome`)
          } else {
            this.$router.push(`/${i18n.locale}/dashboard`)
          }
        } else {
          this.errors.push(
            this.$root.$t('verification.confirmVerificationError')
          )
        }
      } catch (error) {
        const errorMsg =
          error?.response?.data?.err ??
          this.$root.$t('signUp.studentForm.responseError')
        this.errors.push(errorMsg)
      }
      this.isSubmittingVerification = false
      this.errors = []
    },
    signUpWithGoogle() {
      AnalyticsService.captureEvent(EVENTS.USER_CLICKED_SIGN_UP_WITH_GOOGLE)
      localStorage.setItem('isSSOSignUpRedirect', true)
      const data = {
        email: this.eligibility.email,
        currentGrade: this.trimCurrentGrade,
        gradeLevel: this.trimCurrentGrade,
        highSchoolId: this.eligibility.highSchool.upchieveId,
        schoolId: this.eligibility.highSchool.upchieveId,
        referredByCode: window.localStorage.getItem('upcReferredByCode'),
        studentPartnerOrg: this.partnerKey,
        zipCode: this.eligibility.zipCode,
      }
      const params = new URLSearchParams(data).toString()
      const url = `${config.serverRoot}/auth/register/google/student?${params}`
      window.location.replace(url)
    },
    hasFormValidationError(el) {
      return !!el.$errors.length
    },
    cannotSubmitForm(el) {
      return !!el.$errors.length || !!el.$silentErrors.length
    },
    getFormValidationError(el) {
      return el.$errors.map(e => e.$message).join(', ')
    },
  },
}
</script>

<style lang="scss" scoped>
.asText {
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
}

.disableButton {
  cursor: default;
  color: #a8aeb7;
}

p.small-paragraph {
  color: $c-soft-black;
  font-size: 14px;
  margin-top: 25px;
}

.international-availability-info,
.international-contact-us {
  font-weight: 500;
  margin-top: 24px;
}

.international-contact-us {
  font-style: italic;
  margin-top: 0.5em;
}

.uc-form-text {
  margin-top: 10px;

  &.below {
    margin-top: 20px;
  }
}

.break-line-container {
  margin-top: 16px;
}

.break-line {
  background-color: $c-secondary-grey;
  height: 1px;
}

.or-text {
  color: $c-secondary-grey;
  font-style: italic;
  padding: 0 10px;
  margin: 0;
}

.terms-text {
  font-size: 14px;
  font-style: italic;
  margin: 4px 0;
  text-align: center;
}

.phone-input {
  margin: 10px 0 2px;
}

html.is-rtl {
  .phone-input {
    direction: ltr;
  }
}
</style>
